import { omit } from 'es-toolkit'
import { RxCollection } from 'rxdb'
import { replicateRxCollection } from 'rxdb/plugins/replication'
import { toast } from 'ui/sonner'

import { apiClient } from '~/api/client'
import { config } from '~/config/env'
import { Card } from '~/db/types'
import i18n from '~/i18n'

type ReplicationCallbacks = {
  onError?: (error: Error) => void
  onFinish?: () => void
  onStart?: () => void
}

export const replicateCards = (
  cardsCollection: RxCollection,
  planId: number,
  { onStart, onError, onFinish }: ReplicationCallbacks = {},
) => {
  const free_plan_id = config.VITE_FREE_PLAN_ID

  if (planId === free_plan_id) return

  const replication = replicateRxCollection<Card, { pushedAt?: number }>({
    collection: cardsCollection,
    replicationIdentifier: config.VITE_API_BASE,
    live: false,
    retryTime: 5 * 1000,
    waitForLeadership: true,
    autoStart: false,
    deletedField: 'deleted',
    push: {
      /**
       * Push handler
       */
      async handler(docs) {
        try {
          /**
           * Push the local documents to a remote REST server.
           */
          const { data = [] } = await apiClient.POST('/replication/push', {
            method: 'POST',
            body: { docs },
          })

          /**
           * Contains an array with all conflicts that appeared during this push.
           * If there were no conflicts, return an empty array.
           */
          //! rx-db types are not smart enough to detect deleteField key override
          // TODO: try updating rx-db to address this
          return data.map(doc => ({
            ...doc,
            snoozedUntilSource: doc.snoozedUntilSource ?? undefined,
            snoozeCountSource: doc.snoozeCountSource ?? 0,
            snoozedUntilLearn: doc.snoozedUntilLearn ?? undefined,
            snoozeCountLearn: doc.snoozeCountLearn ?? 0,
            _deleted: !!doc.deleted,
          }))
        } catch (error) {
          toast.error(i18n.t('Network error'))
          console.error({ message: 'Network error', error })

          throw error
        }
      },
      batchSize: config.VITE_PUSH_BATCH_SIZE,
    },
    pull: {
      /**
       * Pull handler
       */
      async handler(lastCheckpoint, batchSize) {
        const minPushedAt = lastCheckpoint?.pushedAt || 0
        try {
          /**
           * In this example we replicate with a remote REST server
           */
          const { data = [] } = await apiClient.GET('/replication/pull', {
            params: {
              query: {
                minPushedAt,
                limit: batchSize,
              },
            },
          })

          const lastDoc = data.at(-1)

          const checkpoint = lastDoc ? { pushedAt: lastDoc.pushedAt } : (lastCheckpoint ?? null)

          return {
            /**
             * Contains the pulled documents from the remote.
             * Notice: If documentsFromRemote.length < batchSize,
             * then RxDB assumes that there are no more un-replicated documents
             * on the backend, so the replication will switch to 'Event observation' mode.
             */
            //! rx-db types are not smart enough to detect deleteField key override
            documents: data.map(doc => ({
              ...omit(doc, ['pushedAt']),
              snoozedUntilSource: doc.snoozedUntilSource ?? undefined,
              snoozeCountSource: doc.snoozeCountSource ?? 0,
              snoozedUntilLearn: doc.snoozedUntilLearn ?? undefined,
              snoozeCountLearn: doc.snoozeCountLearn ?? 0,
              _deleted: !!doc.deleted,
            })),
            /**
             * The last checkpoint of the returned documents.
             * On the next call to the pull handler,
             * this checkpoint will be passed as 'lastCheckpoint'
             */
            checkpoint,
          }
        } catch (error) {
          toast.error(i18n.t('Network error'))
          console.error({ message: 'Network error', error })

          throw error
        }
      },
      batchSize: config.VITE_PULL_BATCH_SIZE,
    },
  })
  if (onStart) replication.active$.subscribe(onStart)

  if (onError) replication.error$.subscribe(onError)

  if (onFinish) replication.canceled$.subscribe(onFinish)

  return replication
}
